// ── logs-collection: useCollection client adapter for /history ───────────────

import { useMemo } from "react";
import {
  Badge,
  StatusDot,
  useCollection,
  type CollectionColumnDef,
  type CollectionFilterDef,
  type UseCollectionResult
} from "@vjal/ui";

import type { CoachingHistoryEventDto } from "../../services/coaching-dtos";
import { readDirectoryInitialState } from "../directory-collection-state";
import {
  eventActionLabel,
  eventActorLabel,
  eventCategoryLabel,
  eventSummary,
  isDeniedActionEvent,
  LOG_VIEWS
} from "./logs-helpers";

export const LOGS_PAGE_SIZE = 25;
export const LOGS_DEFAULT_SORT = { id: "occurred", dir: "desc" as const };
const UNMAPPED_VIEW_KEY = "__unscoped__";

export type LogRow = CoachingHistoryEventDto & Record<string, unknown>;

export function logViewFilterKey(row: LogRow): string {
  const category = row.category ?? "";
  for (const view of LOG_VIEWS) {
    if (view.id === "audit" || view.categories.length === 0) continue;
    if (category && view.categories.includes(category)) return view.id;
  }
  return UNMAPPED_VIEW_KEY;
}

export function buildLogsFilterDefs(): CollectionFilterDef<LogRow>[] {
  return [
    {
      id: "view",
      label: "Log view",
      type: "select",
      placeholder: LOG_VIEWS[0].label,
      getValue: (row) => logViewFilterKey(row),
      options: LOG_VIEWS.filter((view) => view.id !== "audit").map((view) => ({
        value: view.id,
        label: view.label
      }))
    }
  ];
}

export function buildLogsCollectionColumns(): CollectionColumnDef<LogRow>[] {
  return [
    {
      id: "action",
      header: "Action",
      sortable: true,
      sortAccessor: (row) => row.action ?? "",
      searchValue: (row) => `${row.action ?? ""} ${eventSummary(row)}`,
      priority: 1,
      cell: (row) => (
        <Badge variant={isDeniedActionEvent(row) ? "warning" : "info"}>{eventActionLabel(row.action)}</Badge>
      )
    },
    {
      id: "category",
      header: "Category",
      sortable: true,
      sortAccessor: (row) => row.category ?? "",
      priority: 2,
      cell: (row) => <span className="text-sm text-ink-muted">{eventCategoryLabel(row.category)}</span>
    },
    {
      id: "actor",
      header: "Actor",
      sortable: true,
      sortAccessor: (row) => eventActorLabel(row),
      priority: 2,
      cell: (row) => eventActorLabel(row)
    },
    {
      id: "summary",
      header: "Summary",
      sortable: true,
      sortAccessor: (row) => eventSummary(row),
      priority: 1,
      cell: (row) => eventSummary(row)
    },
    {
      id: "occurred",
      header: "Occurred",
      sortable: true,
      sortAccessor: (row) => row.occurred_at ?? "",
      priority: 2,
      cell: (row) => <span className="text-sm text-ink-muted">{row.occurred_at ?? "—"}</span>
    },
    {
      id: "auditState",
      header: "Audit",
      sortable: false,
      priority: 3,
      cell: (row) => (
        <StatusDot
          tone={isDeniedActionEvent(row) ? "warn" : "info"}
          label={isDeniedActionEvent(row) ? "denied-action recorded" : "append-only"}
        />
      )
    }
  ];
}

export function useLogsCollection(
  rows: CoachingHistoryEventDto[],
  routePath?: string
): UseCollectionResult<LogRow> {
  const filters = useMemo(() => buildLogsFilterDefs(), []);
  const columns = useMemo(() => buildLogsCollectionColumns(), []);
  const initialState = useMemo(
    () => ({ sort: LOGS_DEFAULT_SORT, ...readDirectoryInitialState(routePath, LOGS_PAGE_SIZE) }),
    [routePath]
  );

  return useCollection<LogRow>({
    mode: "client",
    rows: rows as LogRow[],
    columns,
    filters,
    pageSize: LOGS_PAGE_SIZE,
    urlSync: "query",
    views: "coaching-history-logs",
    initialState,
    getRowKey: (row) => row.id
  });
}
